import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Edit, Trash2, Cpu, Search, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { rotuloStatusAtivo } from '@/lib/i18n';
import { EVENTO_DOCUMENTOS_MAQUINA, resumirDocumentosMaquina } from '@/lib/mock-documentos-maquina';
import { ResumoDocumentos } from '@/components/maquinas/resumo-documentos';

export function MaquinasList() {
  const navigate = useNavigate();
  const { projeto } = useProjeto();
  const { t } = useI18n();
  const [maquinas, setMaquinas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ busca: '', tipo: '', area: '' });
  const [paginacao, setPaginacao] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [modalRemover, setModalRemover] = useState<{
    aberto: boolean; id: string; nome: string; erro: string;
  }>({ aberto: false, id: '', nome: '', erro: '' });

  useEffect(() => {
    if (projeto) carregarMaquinas();
  }, [projeto, filtros, paginacao.page]);

  const carregarMaquinas = async () => {
    if (!projeto) return;
    setLoading(true);
    try {
      const response = await apiService.listarAtivos({
        projetoId: projeto.id,
        busca: filtros.busca || undefined,
        tipo: filtros.tipo || undefined,
        area: filtros.area || undefined,
        page: paginacao.page,
        limit: paginacao.limit,
      });
      if (response.success && response.data) {
        setMaquinas(Array.isArray(response.data) ? response.data : []);
        if (response.pagination) {
          setPaginacao((prev) => ({
            ...prev,
            total: response.pagination!.total,
            totalPages: response.pagination!.totalPages,
          }));
        }
      } else {
        // Falha não é lista vazia: apagar o que está na tela faz parecer que as
        // máquinas sumiram do projeto.
        console.error('Erro ao carregar máquinas:', response.message);
        toast.error(response.message || t('maquinas.lista.erro.carregar'));
      }
    } catch (error) {
      console.error('Erro ao carregar máquinas:', error);
      toast.error(
        navigator.onLine
          ? t('maquinas.lista.erro.servidor')
          : t('maquinas.lista.erro.offline')
      );
    } finally {
      setLoading(false);
    }
  };

  // Recalcula quando a lista muda e quando alguém mexe no acervo em outra tela.
  const [versaoDocumentos, setVersaoDocumentos] = useState(0);
  useEffect(() => {
    const aoMudar = () => setVersaoDocumentos((v) => v + 1);
    window.addEventListener(EVENTO_DOCUMENTOS_MAQUINA, aoMudar);
    return () => window.removeEventListener(EVENTO_DOCUMENTOS_MAQUINA, aoMudar);
  }, []);

  const resumoPorMaquina = useMemo(() => {
    const mapa = new Map<string, ReturnType<typeof resumirDocumentosMaquina>>();
    maquinas.forEach((m) => mapa.set(m.id, resumirDocumentosMaquina(m.id)));
    return mapa;
  }, [maquinas, versaoDocumentos]);

  const abrirMaquina = (maquina: any) => {
    // A linha vai junto: a ficha aproveita as apreciações que a listagem já traz.
    navigate(`/maquinas/${maquina.id}`, { state: { maquina } });
  };

  const handleFiltroChange = (campo: string, valor: string) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPaginacao((prev) => ({ ...prev, page: 1 }));
  };

  const handleRemover = (id: string, nome: string) => {
    setModalRemover({ aberto: true, id, nome, erro: '' });
  };

  const handleConfirmarRemover = async () => {
    try {
      const response = await apiService.removerAtivo(modalRemover.id);
      if (response.success) {
        setModalRemover({ aberto: false, id: '', nome: '', erro: '' });
        carregarMaquinas();
      } else {
        setModalRemover((prev) => ({
          ...prev,
          erro: response.message || t('maquinas.lista.remover.erro'),
        }));
      }
    } catch (error) {
      setModalRemover((prev) => ({ ...prev, erro: t('maquinas.lista.remover.erro') }));
    }
  };

  const inputClass =
    'block w-full px-3 py-2.5 text-sm border border-rg-line dark:border-schmersal-gray-dark rounded-lg ' +
    'focus:outline-none focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue ' +
    'bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white ' +
    'placeholder-schmersal-gray-medium dark:placeholder-schmersal-gray-medium transition-all duration-200';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-schmersal-blue dark:text-white">
            {t('maquinas.lista.titulo')}
          </h1>
          <p className="mt-1 text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
            {t('maquinas.lista.subtitulo')}
            {paginacao.total > 0 && (
              <span className="ml-2 font-bold text-schmersal-blue dark:text-schmersal-blue-light">
                ({paginacao.total})
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <button
            onClick={carregarMaquinas}
            disabled={loading}
            className="inline-flex items-center justify-center px-4 py-2.5 border border-schmersal-blue rounded-lg
              shadow-md text-sm font-semibold text-schmersal-blue dark:text-white bg-white dark:bg-schmersal-gray-darker
              hover:bg-schmersal-blue hover:text-white disabled:opacity-50 transition-all duration-200 w-full sm:w-auto"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('comum.acao.atualizar')}
          </button>
          <Link
            to="/maquinas/nova"
            className="inline-flex items-center justify-center px-5 py-2.5 border-2 border-transparent rounded-lg
              shadow-md text-sm font-semibold text-white bg-schmersal-blue hover:bg-schmersal-blue-dark
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue
              transition-all duration-200 w-full sm:w-auto"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('maquinas.lista.novaMaquina')}
          </Link>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">{t('maquinas.lista.filtro.busca')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-schmersal-gray-medium" />
              <input
                type="text"
                value={filtros.busca}
                onChange={(e) => handleFiltroChange('busca', e.target.value)}
                placeholder={t('maquinas.lista.filtro.buscaPlaceholder')}
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">{t('comum.rotulo.tipo')}</label>
            <input
              type="text"
              value={filtros.tipo}
              onChange={(e) => handleFiltroChange('tipo', e.target.value)}
              placeholder={t('maquinas.lista.filtro.tipoPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-2">{t('comum.rotulo.area')}</label>
            <input
              type="text"
              value={filtros.area}
              onChange={(e) => handleFiltroChange('area', e.target.value)}
              placeholder={t('maquinas.lista.filtro.areaPlaceholder')}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-schmersal-blue mx-auto mb-3" />
              <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light text-sm">{t('maquinas.lista.carregando')}</p>
            </div>
          </div>
        ) : maquinas.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-center px-4">
            <Cpu className="h-12 w-12 text-schmersal-gray-medium mb-3 opacity-40" />
            <p className="text-schmersal-gray-dark dark:text-white font-semibold mb-1">{t('maquinas.lista.vazio.titulo')}</p>
            <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light text-sm">
              {filtros.busca || filtros.tipo || filtros.area
                ? t('maquinas.lista.vazio.comFiltro')
                : t('maquinas.lista.vazio.semFiltro')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y-2 divide-rg-hairline dark:divide-schmersal-gray-dark">
              <thead className="bg-schmersal-gray-light dark:bg-schmersal-gray-darkest">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider">
                    {t('maquinas.lista.coluna.areaLocal')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider">
                    {t('maquinas.lista.coluna.equipamento')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider hidden md:table-cell">
                    {t('comum.rotulo.tipo')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider hidden lg:table-cell">
                    {t('maquinas.lista.coluna.tag')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider hidden sm:table-cell">
                    {t('comum.rotulo.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider hidden md:table-cell">
                    {t('documentos.colunaDashboard')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider hidden sm:table-cell">
                    {t('maquinas.lista.coluna.apreciacoes')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-schmersal-gray-dark dark:text-white uppercase tracking-wider">
                    {t('comum.rotulo.acoes')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-schmersal-gray-darker divide-y-2 divide-rg-hairline dark:divide-schmersal-gray-dark">
                {maquinas.map((m) => (
                  <tr
                    key={m.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => abrirMaquina(m)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        abrirMaquina(m);
                      }
                    }}
                    title={t('maquinas.lista.acao.abrir')}
                    className="cursor-pointer hover:bg-schmersal-gray-light dark:hover:bg-schmersal-gray-darkest transition-colors duration-150"
                  >
                    {/* Área / Local */}
                    <td className="px-6 py-4">
                      <p className="text-sm font-bold text-schmersal-gray-dark dark:text-white">{m.area || '—'}</p>
                      {m.local && (
                        <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">{m.local}</p>
                      )}
                    </td>

                    {/* Equipamento */}
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-schmersal-blue/10 dark:bg-schmersal-blue/20 flex items-center justify-center mt-0.5">
                          <Cpu className="h-4 w-4 text-schmersal-blue dark:text-schmersal-blue-light" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-schmersal-gray-dark dark:text-white">
                            {m.equipamento || '—'}
                          </p>
                          {m.item && (
                            <p className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light mt-0.5">
                              {t('maquinas.lista.itemPrefixo', { item: m.item })}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Tipo */}
                    <td className="px-6 py-4 hidden md:table-cell">
                      {m.tipo ? (
                        <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-schmersal-blue/10 dark:bg-schmersal-blue/20 text-schmersal-blue dark:text-schmersal-blue-light">
                          {m.tipo}
                        </span>
                      ) : (
                        <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">—</span>
                      )}
                    </td>

                    {/* TAG */}
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-sm text-schmersal-gray-dark dark:text-white">
                        {m.tag || '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 hidden sm:table-cell">
                      {m.status ? (
                        <span className="inline-block px-2.5 py-1 text-xs font-semibold rounded-full bg-schmersal-yellow/20 text-schmersal-blue dark:text-schmersal-yellow">
                          {rotuloStatusAtivo(t, m.status)}
                        </span>
                      ) : (
                        <span className="text-xs text-schmersal-gray-medium dark:text-schmersal-gray-light">—</span>
                      )}
                    </td>

                    {/* Documentos (protótipo) */}
                    <td className="px-6 py-4 hidden md:table-cell">
                      <ResumoDocumentos resumo={resumoPorMaquina.get(m.id) ?? { total: 0, pendentes: 0, emAnalise: 0, entregues: 0, atrasados: 0, tiposPendentes: [] }} />
                    </td>

                    {/* Apreciações */}
                    <td className="px-6 py-4 text-center hidden sm:table-cell">
                      <span className={`inline-block px-2.5 py-1 text-xs font-bold rounded-full ${
                        (m.apreciacoes?.length || 0) > 0
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-schmersal-gray-light dark:bg-schmersal-gray-darkest text-schmersal-gray-medium dark:text-schmersal-gray-light'
                      }`}>
                        {m.apreciacoes?.length || 0}
                      </span>
                    </td>

                    {/* Ações */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/maquinas/${m.id}/editar`}
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 rounded-lg text-schmersal-gray-medium hover:text-schmersal-blue hover:bg-schmersal-blue/10 transition-all duration-200"
                          title={t('maquinas.lista.acao.editar')}
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemover(m.id, m.equipamento || m.id);
                          }}
                          className="p-2 rounded-lg text-schmersal-gray-medium hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
                          title={t('maquinas.lista.acao.remover')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <ChevronRight className="h-4 w-4 text-schmersal-gray-medium" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {paginacao.totalPages > 1 && (
              <div className="px-6 py-4 flex items-center justify-between border-t-2 border-schmersal-gray-light dark:border-schmersal-gray-dark">
                <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light">
                  {t('maquinas.lista.paginacao.resumo', {
                    pagina: paginacao.page,
                    totalPaginas: paginacao.totalPages,
                    total: paginacao.total,
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPaginacao((prev) => ({ ...prev, page: prev.page - 1 }))}
                    disabled={paginacao.page <= 1}
                    className="px-3 py-1.5 text-sm font-semibold border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                      text-schmersal-gray-dark dark:text-white hover:border-schmersal-blue hover:text-schmersal-blue
                      disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {t('maquinas.lista.paginacao.anterior')}
                  </button>
                  <button
                    onClick={() => setPaginacao((prev) => ({ ...prev, page: prev.page + 1 }))}
                    disabled={paginacao.page >= paginacao.totalPages}
                    className="px-3 py-1.5 text-sm font-semibold border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                      text-schmersal-gray-dark dark:text-white hover:border-schmersal-blue hover:text-schmersal-blue
                      disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {t('maquinas.lista.paginacao.proxima')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de confirmação de remoção */}
      {modalRemover.aberto && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 dark:bg-black/70"
              onClick={() => setModalRemover({ aberto: false, id: '', nome: '', erro: '' })}
            />
            <div className="relative bg-white dark:bg-schmersal-gray-darker rounded-xl shadow-2xl border border-rg-line dark:border-schmersal-gray-dark p-6 w-full max-w-md">
              <h3 className="text-lg font-bold text-schmersal-gray-dark dark:text-white mb-2">
                {t('maquinas.lista.remover.titulo')}
              </h3>
              <p className="text-sm text-schmersal-gray-medium dark:text-schmersal-gray-light mb-4">
                {t('maquinas.lista.remover.confirmacao', { nome: modalRemover.nome })}{' '}
                {t('comum.confirmacao.acaoIrreversivel')}
              </p>
              {modalRemover.erro && (
                <p className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-3">
                  {modalRemover.erro}
                </p>
              )}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setModalRemover({ aberto: false, id: '', nome: '', erro: '' })}
                  className="px-4 py-2 text-sm font-semibold border border-rg-line dark:border-schmersal-gray-dark rounded-lg
                    text-schmersal-gray-dark dark:text-white hover:border-schmersal-blue hover:text-schmersal-blue transition-all duration-200"
                >
                  {t('comum.acao.cancelar')}
                </button>
                <button
                  onClick={handleConfirmarRemover}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border-2 border-transparent bg-red-600 text-white hover:bg-red-700 transition-all duration-200"
                >
                  {t('comum.acao.remover')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
